import React, { useState, useEffect, useRef } from "react";
import {
	Dialog,
	DialogContent,
	Typography,
	CircularProgress,
	Box,
} from "@material-ui/core";
import { Input } from "@components/ui/input";
import AccentButton from "@components/ui/AccentButton";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";
import useLanguageStore from "@stores/languageStore";

const LeadGate = () => {
	const { t } = useTranslation();
	const { language } = useLanguageStore();
	const isRtl = language === "he";
	const [isOpen, setIsOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");
	const formRef = useRef();

	useEffect(() => {
		// Always show the gate when the component mounts (on page load/refresh)
		setIsOpen(true);
	}, []);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		const formData = new FormData(formRef.current);
		const name = formData.get("from_name");
		const phone = formData.get("phone");

		if (!name || !phone) {
			setError(t("leadGate.error"));
			return;
		}

		setIsSubmitting(true);

		const serviceID =
			import.meta.env.VITE_EMAILJS_SERVICE_ID || "YOUR_SERVICE_ID";
		const templateID =
			import.meta.env.VITE_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID";
		const publicKey =
			import.meta.env.VITE_EMAILJS_PUBLIC_KEY || "YOUR_PUBLIC_KEY";

		if (serviceID === "YOUR_SERVICE_ID") {
			console.warn("EmailJS keys are missing.");
			// For development/demo purposes, we'll just succeed if keys are missing
			// In production, this should probably fail or be handled differently
			// but we don't want to block the user if config is missing.
			setIsOpen(false);
			setIsSubmitting(false);
			return;
		}

		try {
			// We are using the same template as contact form.
			// We'll inject a message to indicate source.
			const templateParams = {
				from_name: name,
				phone: phone,
				message: "Lead from Design Gate (Obituary/Tombstone Builder)",
				// email is removed from requirements
			};

			await emailjs.send(serviceID, templateID, templateParams, publicKey);
            
            // Report conversion to Google Ads (same as contact form)
            if (window.gtag_report_conversion) {
                window.gtag_report_conversion();
            }

			// We do NOT save to localStorage anymore, so it will ask again on refresh/navigation
			setIsOpen(false);
		} catch (err) {
			console.error("Lead Gate submission failed:", err);
			setError(t("common.error")); // Or a more specific error
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<Dialog
			open={isOpen}
			disableEscapeKeyDown
			// We don't provide onClose to force interaction
			maxWidth="sm"
			fullWidth
			PaperProps={{
				style: {
					borderRadius: "12px",
					padding: "16px",
				},
			}}
		>
			<DialogContent>
				<div className={`text-center mb-6 ${isRtl ? "rtl" : "ltr"}`}>
					<Typography variant="h5" component="h2" className="font-bold mb-2">
						{t("leadGate.title")}
					</Typography>
					<Typography variant="body1" className="text-gray-600">
						{t("leadGate.subtitle")}
					</Typography>
				</div>

				<form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
					<Input
						label={t("leadGate.name")}
						name="from_name"
						placeholder={t("contact.form.placeholder.name")}
						dir={isRtl ? "rtl" : "ltr"}
						required
						disabled={isSubmitting}
					/>
					<Input
						label={t("leadGate.phone")}
						type="tel"
						name="phone"
						placeholder={t("contact.form.placeholder.phone")}
						dir={isRtl ? "rtl" : "ltr"}
						required
						disabled={isSubmitting}
					/>

					{error && (
						<Typography color="error" variant="body2" align="center">
							{error}
						</Typography>
					)}

					<AccentButton
						type="submit"
						size="large"
						fullWidth
						className="justify-center py-3"
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<CircularProgress size={24} color="inherit" />
						) : (
							t("leadGate.submit")
						)}
					</AccentButton>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default LeadGate;

